import type { CSSProperties, ReactNode } from 'react';
import type { DayWindow, DayWindows, Panchanga } from '@astrally/astro-engine';
import { GRAHA_NAMES, type GrahaBody, type Planet } from '@astrally/shared';
import { useI18n } from '../../i18n';
import { InfoDot } from '../../components/common';
import { Term } from '../../components/Term';
import { clockIn, type ReaderPlace } from './horoscopeData';
import {
  AVOID_WORD,
  DAYLIGHT_WORD,
  FAVOURED_WORD,
  LIMB_UNTIL,
  NOW_WORD,
  NO_ABHIJIT,
  PAKSHA_KRISHNA,
  PAKSHA_SHUKLA,
  PANCHANGA_INTRO,
  SUNRISE_ESTIMATED,
  VISHTI_NOTE,
  WINDOWS_INTRO,
  WINDOWS_TITLE,
} from './copy';

/**
 * The day itself — panchanga plus the two windows people actually check.
 *
 * Deterministic calendar maths from `@astrally/astro-engine`, computed at the
 * reader's place. No chart is needed to render any of it and no AI is involved,
 * so this appears on the first frame.
 *
 * Every one of these five words is unknown to the audience this app is written
 * for, so every one of them carries an InfoDot. The card assumes zero
 * background: it says what a panchanga IS before listing one.
 *
 * COLOUR IS NEVER THE ONLY SIGNAL on the strip below. Each block carries a
 * distinct pattern (diagonal hatch = avoid, vertical pinstripe = favoured) and,
 * more importantly, every window is also printed underneath by name, in words,
 * with its clock times. Someone who cannot see the strip at all loses nothing.
 */

/* --- fills ----------------------------------------------------------------- */

const FILL_AVOID: CSSProperties = {
  background: 'repeating-linear-gradient(135deg, var(--danger) 0 2px, var(--danger-soft) 2px 7px)',
  border: '1px solid var(--danger-line)',
};

const FILL_GOOD: CSSProperties = {
  background: 'repeating-linear-gradient(90deg, var(--ok) 0 2px, var(--ok-soft) 2px 7px)',
  border: '1px solid var(--ok)',
};

/**
 * The engine hands the vaara's ruler as a planet id ('sun'…); the reader gets
 * the graha's own name (Surya…). Every weekday lord is one of the seven
 * classical grahas, so the fallback only guards an engine change.
 */
function grahaName(planet: Planet): string {
  return planet in GRAHA_NAMES ? GRAHA_NAMES[planet as GrahaBody] : planet;
}

/* --- limbs ----------------------------------------------------------------- */

/**
 * One of the five. `until` is the real end of that limb, which is the thing a
 * bare name never tells you — a tithi does not run midnight to midnight, and
 * seeing it expire at 14:32 is what makes the concept click.
 */
function Limb({
  label,
  term,
  value,
  until,
  extra,
}: {
  label: string;
  term: string;
  value: string;
  until?: string;
  extra?: ReactNode;
}) {
  return (
    <div
      className="spread"
      style={{ alignItems: 'baseline', gap: 8, padding: '7px 0', borderTop: '1px solid var(--border)' }}
    >
      <span className="tiny faint" style={{ display: 'flex', alignItems: 'center', gap: 2, flexShrink: 0 }}>
        {label}
        <InfoDot term={term} />
      </span>
      <span className="small" style={{ textAlign: 'right', minWidth: 0 }}>
        <span style={{ color: 'var(--mode-ink)', fontWeight: 600 }}>{value}</span>
        {until && (
          <span className="tiny faint" style={{ display: 'block', marginTop: 1 }}>
            {LIMB_UNTIL} {until}
          </span>
        )}
        {extra}
      </span>
    </div>
  );
}

/* --- windows --------------------------------------------------------------- */

function WindowRow({
  name,
  term,
  window: w,
  timezone,
  avoid,
}: {
  name: string;
  term?: string;
  window: DayWindow;
  timezone: string;
  avoid: boolean;
}) {
  return (
    <div
      className="row"
      style={{ alignItems: 'stretch', gap: 10, padding: '8px 0', borderTop: '1px solid var(--border)' }}
    >
      <span
        aria-hidden
        style={{ width: 10, minWidth: 10, borderRadius: 4, alignSelf: 'stretch', ...(avoid ? FILL_AVOID : FILL_GOOD) }}
      />
      <div style={{ flex: 1, minWidth: 0 }}>
        <div className="spread" style={{ alignItems: 'baseline', gap: 8 }}>
          <span className="small" style={{ display: 'flex', alignItems: 'center', gap: 2, fontWeight: 600 }}>
            {name}
            {term && <InfoDot term={term} />}
          </span>
          <span className="tiny muted" style={{ whiteSpace: 'nowrap' }}>
            {clockIn(timezone, w.startUtc)}–{clockIn(timezone, w.endUtc)}
          </span>
        </div>
        <div
          className="tiny"
          style={{ color: avoid ? 'var(--danger)' : 'var(--ok)', fontWeight: 600, marginTop: 2 }}
        >
          {avoid ? AVOID_WORD : FAVOURED_WORD}
        </div>
        <div className="tiny muted" style={{ marginTop: 3, lineHeight: 1.5 }}>
          {w.note}
        </div>
      </div>
    </div>
  );
}

/* --- the card -------------------------------------------------------------- */

export interface PanchangaCardProps {
  panchanga: Panchanga;
  windows: DayWindows;
  place: ReaderPlace;
  /** Draws a "now" needle when it falls inside the daylight span. */
  now: Date;
}

export function PanchangaCard({ panchanga, windows, place, now }: PanchangaCardProps) {
  const { t } = useI18n();
  const tz = place.timezone;
  const { day } = windows;

  // The strip spans real daylight, because rahu kaal and abhijit are both cut
  // from it — one eighth of it and one muhurta around solar noon respectively.
  const spanStart = day.sunriseUtc.getTime();
  const spanEnd = Math.max(day.sunsetUtc.getTime(), spanStart + 1);
  const spanMs = spanEnd - spanStart;
  const pct = (ms: number) => ((ms - spanStart) / spanMs) * 100;
  const blockAt = (w: { startUtc: Date; endUtc: Date }): CSSProperties => ({
    position: 'absolute',
    top: 0,
    bottom: 0,
    left: `${Math.max(0, Math.min(100, pct(w.startUtc.getTime())))}%`,
    width: `${Math.max(0.8, pct(w.endUtc.getTime()) - pct(w.startUtc.getTime()))}%`,
  });

  const nowMs = now.getTime();
  const nowInside = nowMs >= spanStart && nowMs <= spanEnd;

  // The strip is decorative; this sentence is what a screen reader gets.
  const stripSummary = [
    `${DAYLIGHT_WORD} ${clockIn(tz, day.sunriseUtc)} to ${clockIn(tz, day.sunsetUtc)}.`,
    `${t('panchanga.rahuKaal')} ${clockIn(tz, windows.rahuKaal.startUtc)} to ${clockIn(tz, windows.rahuKaal.endUtc)}, ${AVOID_WORD.toLowerCase()}.`,
    windows.abhijit
      ? `${t('panchanga.abhijit')} ${clockIn(tz, windows.abhijit.startUtc)} to ${clockIn(tz, windows.abhijit.endUtc)}, ${FAVOURED_WORD.toLowerCase()}.`
      : NO_ABHIJIT,
    nowInside ? `${NOW_WORD} ${clockIn(tz, now)}.` : '',
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <div className="card" data-mode="vedic">
      <div className="spread" style={{ marginBottom: 8, alignItems: 'flex-start', gap: 8 }}>
        <span className="mode-chip">
          {panchanga.vaara.name} · {panchanga.vaara.englishName}
          <InfoDot term="vaara" />
        </span>
        <span className="tiny faint" style={{ flexShrink: 0 }}>
          {t('home.sunrise')} {clockIn(tz, day.sunriseUtc)}
        </span>
      </div>
      <div className="mode-rule" style={{ marginBottom: 10 }} />

      <div className="tiny faint" style={{ lineHeight: 1.5, marginBottom: 4 }}>
        {PANCHANGA_INTRO}
      </div>

      {day.estimated && (
        <div className="banner banner--gold tiny" style={{ margin: '8px 0', lineHeight: 1.5 }}>
          {SUNRISE_ESTIMATED}
        </div>
      )}

      <Limb
        label={t('panchanga.tithi')}
        term="tithi"
        value={`${panchanga.tithi.name} (${panchanga.tithi.paksha === 'shukla' ? 'waxing' : 'waning'} day ${panchanga.tithi.numberInPaksha})`}
        until={clockIn(tz, panchanga.tithi.endUtc)}
      />
      <Limb
        label={t('panchanga.vaara')}
        term="vaara"
        value={`${panchanga.vaara.name} · ${panchanga.vaara.englishName}`}
        extra={
          <span className="tiny faint" style={{ display: 'block', marginTop: 1 }}>
            ruled by{' '}
            <Term id={`graha.${grahaName(panchanga.vaara.lord).toLowerCase()}`}>
              {grahaName(panchanga.vaara.lord)}
            </Term>
          </span>
        }
      />
      <Limb
        label={t('panchanga.nakshatra')}
        term="nakshatra"
        value={panchanga.nakshatra.name}
        until={clockIn(tz, panchanga.nakshatra.endUtc)}
      />
      <Limb
        label={t('panchanga.yoga')}
        term="yoga"
        value={panchanga.yoga.name}
        until={clockIn(tz, panchanga.yoga.endUtc)}
      />
      <Limb
        label={t('panchanga.karana')}
        term="karana"
        value={panchanga.karana.name}
        until={clockIn(tz, panchanga.karana.endUtc)}
      />

      <div className="tiny faint" style={{ marginTop: 8, lineHeight: 1.5 }}>
        {panchanga.tithi.paksha === 'shukla' ? PAKSHA_SHUKLA : PAKSHA_KRISHNA}
        {panchanga.karana.isVishti ? ` ${VISHTI_NOTE}` : ''}
      </div>

      {/* --- the day's two windows --- */}
      <div className="divider" style={{ margin: '14px 0 10px' }} />
      <div className="spread" style={{ alignItems: 'center' }}>
        <span className="h3" style={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          {WINDOWS_TITLE}
          <InfoDot term="muhurtha" />
        </span>
        <span className="tiny faint" style={{ flexShrink: 0 }}>
          {clockIn(tz, day.sunriseUtc)}–{clockIn(tz, day.sunsetUtc)}
        </span>
      </div>

      <div
        role="img"
        aria-label={stripSummary}
        style={{
          position: 'relative',
          height: 24,
          marginTop: 10,
          borderRadius: 8,
          overflow: 'hidden',
          border: '1px solid var(--border)',
          background: 'var(--mode-soft)',
        }}
      >
        <div aria-hidden style={{ ...blockAt(windows.rahuKaal), ...FILL_AVOID }} />
        {windows.abhijit && <div aria-hidden style={{ ...blockAt(windows.abhijit), ...FILL_GOOD }} />}
        {nowInside && (
          <div
            aria-hidden
            style={{
              position: 'absolute',
              top: 0,
              bottom: 0,
              left: `${pct(nowMs)}%`,
              width: 2,
              background: 'var(--text)',
            }}
          />
        )}
      </div>

      <div className="wrap" style={{ gap: 10, marginTop: 8 }}>
        <span className="row tiny muted" style={{ gap: 6 }}>
          <span aria-hidden style={{ width: 16, height: 10, minWidth: 16, borderRadius: 3, ...FILL_AVOID }} />
          {AVOID_WORD}
        </span>
        <span className="row tiny muted" style={{ gap: 6 }}>
          <span aria-hidden style={{ width: 16, height: 10, minWidth: 16, borderRadius: 3, ...FILL_GOOD }} />
          {FAVOURED_WORD}
        </span>
        {nowInside && (
          <span className="row tiny muted" style={{ gap: 6 }}>
            <span aria-hidden style={{ width: 2, height: 12, background: 'var(--text)' }} />
            {NOW_WORD} {clockIn(tz, now)}
          </span>
        )}
      </div>

      <WindowRow
        name={t('panchanga.rahuKaal')}
        term="rahu-kaal"
        window={windows.rahuKaal}
        timezone={tz}
        avoid
      />
      {windows.abhijit ? (
        <WindowRow
          name={t('panchanga.abhijit')}
          term="muhurtha"
          window={windows.abhijit}
          timezone={tz}
          avoid={false}
        />
      ) : (
        <div
          className="tiny muted"
          style={{ padding: '8px 0', borderTop: '1px solid var(--border)', lineHeight: 1.55 }}
        >
          {NO_ABHIJIT}
        </div>
      )}

      <div className="tiny faint" style={{ marginTop: 8, lineHeight: 1.5 }}>
        {WINDOWS_INTRO}
      </div>
    </div>
  );
}
